import { Check, ChevronsUpDown } from "lucide-react"
import { memo, useCallback } from "react"
import { useFormContext } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import type { TPersonListItem } from "@/http/people"
import type { TTransactionForm } from "@/lib/schemas/transaction"
import { cn } from "@/lib/utils"

interface IPersonSelectorProps {
  peopleOptions: TPersonListItem[]
  isPeopleFetching: boolean
  isPeopleError: boolean
  selectedPersonLabel: string
  personPopoverOpen: boolean
  onPopoverOpenChange: (open: boolean) => void
  personSearch: string
  onPersonSearchChange: (value: string) => void
}

/**
 * Person selector component with search and dropdown
 * Handles optional person selection for transactions
 */
export const PersonSelector = memo(function PersonSelector({
  peopleOptions,
  isPeopleFetching,
  isPeopleError,
  selectedPersonLabel,
  personPopoverOpen,
  onPopoverOpenChange,
  personSearch,
  onPersonSearchChange,
}: IPersonSelectorProps) {
  const form = useFormContext<TTransactionForm>()

  const handlePersonSelect = useCallback(
    (personId: string, _personName: string) => {
      form.setValue("personId", personId)
      onPopoverOpenChange(false)
    },
    [form, onPopoverOpenChange],
  )

  const handleClearPerson = useCallback(() => {
    form.setValue("personId", "")
    onPopoverOpenChange(false)
  }, [form, onPopoverOpenChange])

  const handlePopoverChange = useCallback(
    (nextOpen: boolean) => {
      onPopoverOpenChange(nextOpen)
      if (!nextOpen) {
        onPersonSearchChange("")
      }
    },
    [onPopoverOpenChange, onPersonSearchChange],
  )

  return (
    <FormField
      control={form.control}
      name="personId"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Pessoa</FormLabel>
          <Popover open={personPopoverOpen} onOpenChange={handlePopoverChange}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={personPopoverOpen}
                  className={cn(
                    "w-full justify-between",
                    !field.value && "text-muted-foreground",
                  )}
                  type="button"
                >
                  {selectedPersonLabel ||
                    (field.value
                      ? "Carregando pessoa..."
                      : "Selecione uma pessoa")}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[--radix-popover-trigger-width] p-0"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Buscar pessoa..."
                  value={personSearch}
                  onValueChange={onPersonSearchChange}
                />
                <CommandList>
                  <CommandEmpty>
                    {isPeopleFetching
                      ? "Carregando..."
                      : isPeopleError
                        ? "Erro ao buscar pessoas."
                        : "Nenhuma pessoa encontrada."}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="none" onSelect={handleClearPerson}>
                      Sem pessoa
                      {!field.value && <Check className="ml-auto h-4 w-4" />}
                    </CommandItem>
                    {peopleOptions.map((person) => {
                      const personName = `${person.firstName} ${person.lastName}`
                      return (
                        <CommandItem
                          key={person.id}
                          value={personName}
                          onSelect={() =>
                            handlePersonSelect(person.id, personName)
                          }
                        >
                          {personName}
                          {field.value === person.id && (
                            <Check className="ml-auto h-4 w-4" />
                          )}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  )
})

PersonSelector.displayName = "PersonSelector"
